import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import PortfolioPageLink from './PortfolioPageLink'
import EaglePerch from './EaglePerch'

const BIRDLIFE = 'https://birdlife.org.au/bird-profiles/white-bellied-sea-eagle/'
const NPARKS = 'https://juronglakegardens.nparks.gov.sg/birds/'
const facts = [
  { title: 'The white comes later.', text: 'Young sea eagles start out brown. They gradually develop that distinctive white-and-grey plumage, reaching their adult colours around their fourth year.', source: BIRDLIFE, label: 'BirdLife Australia' },
  { title: 'A surprising voice.', text: 'That serious face comes with a loud, goose-like honk, especially during the breeding season. A little less cinematic than you might expect.', source: BIRDLIFE, label: 'BirdLife Australia' },
  { title: 'Dinner at the water’s surface.', text: 'Fish and sea snakes are on the menu. Its large talons help it snatch prey straight from the surface of the water.', source: NPARKS, label: 'NParks' },
  { title: 'A home worth returning to.', text: 'Pairs reuse their large stick nests across multiple breeding seasons, refreshing the lining with green leaves and twigs.', source: BIRDLIFE, label: 'BirdLife Australia' },
]

export default function EagleProfile() {
  const stageRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [flight, setFlight] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [hasFrame, setHasFrame] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    const previous = document.title
    document.title = 'The sea eagle — Jax'
    return () => { document.title = previous }
  }, [])

  useEffect(() => {
    const pause = () => videoRef.current?.pause()
    const onVisibility = () => { if (document.hidden) pause() }
    document.addEventListener('visibilitychange', onVisibility)
    const observer = new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) pause() })
    if (stageRef.current) observer.observe(stageRef.current)
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', onVisibility); pause() }
  }, [])

  const play = () => {
    const video = videoRef.current
    if (!video) return
    setError(false)
    if (video.ended) video.currentTime = 0
    void video.play().catch(() => { setError(true); setPlaying(false) })
  }

  const changePose = () => {
    if (flight) {
      videoRef.current?.pause()
      setFlight(false)
      setHasFrame(false)
      setPlaying(false)
      setError(false)
    } else {
      // Attach the source within the gesture, including on mobile. No video download until asked.
      flushSync(() => setFlight(true))
      play()
    }
  }

  return (
    <div className="eagle-profile">
      <a className="eagle-skip" href="#main-content">Skip to content</a>
      <div className="h-[2px] bg-jax-accent" />
      <header className="border-b border-jax-navy">
        <nav className="portfolio-navigation" aria-label="Site navigation">
          <PortfolioPageLink className="eagle-wordmark" to="/about">JAX.DEV</PortfolioPageLink>
          <PortfolioPageLink className="eagle-back" to="/about">Back to Jax</PortfolioPageLink>
        </nav>
      </header>

      <main id="main-content" tabIndex={-1}>
        <section className="eagle-introduction" aria-labelledby="eagle-title">
          <p className="eagle-eyebrow">The bird behind the site</p>
          <h1 id="eagle-title">White-breasted<br className="eagle-title-break" /> sea eagle<span>.</span></h1>
          <p className="eagle-species">Also known as the white-bellied sea eagle · <a href="https://www.nparks.gov.sg/florafaunaweb/fauna/6/8/685" target="_blank" rel="noopener noreferrer"><i>Haliaeetus leucogaster</i></a></p>

          <div className="eagle-exhibit">
            <div className="eagle-specimen-note eagle-specimen-left">
              <span className="eagle-eyebrow">Room to soar</span>
              <strong>1.8–2 m</strong>
              <a href={BIRDLIFE} target="_blank" rel="noopener noreferrer">Wingspan</a>
            </div>
            <div className={`eagle-stage${hasFrame ? ' is-in-flight' : ''}`} ref={stageRef}>
              <EaglePerch hidden={hasFrame} />
              {flight && <video
                ref={videoRef}
                className="eagle-flight-study"
                src="/media/eagle/hero-flight.mp4"
                aria-label="Illustrated sea eagle gliding with its wings spread"
                aria-hidden={!hasFrame}
                muted playsInline preload="none" disablePictureInPicture
                onPlaying={() => { setPlaying(true); setHasFrame(true); setError(false) }}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onError={() => { setError(true); setPlaying(false); setHasFrame(false) }}
              />}
            </div>
            <div className="eagle-specimen-note eagle-specimen-right">
              <span className="eagle-eyebrow">Close to home</span>
              <strong>Singapore</strong>
              <a href={NPARKS} target="_blank" rel="noopener noreferrer">Our largest resident raptor</a>
            </div>
          </div>

          <div className="eagle-stage-controls">
            <button type="button" onClick={changePose}>{flight ? 'Back to perch' : 'Watch it soar'}</button>
            {flight && <button type="button" onClick={() => playing ? videoRef.current?.pause() : play()}>{playing ? 'Pause flight' : 'Play flight'}</button>}
          </div>
          <p className="eagle-stage-caption" role="status">{error ? 'The flight couldn’t play. You can retry or return to the portrait.' : 'A small study in quiet presence.'}</p>
        </section>

        <div className="eagle-reading">
          <section className="eagle-why" aria-labelledby="eagle-why-title">
            <p className="eagle-eyebrow">Why this bird</p>
            <h2 id="eagle-why-title">Quiet focus. A wider view.</h2>
            <p>I like the balance: a bird that can fill the sky, yet look completely at ease on a branch. There’s presence in it without the need for constant motion.</p>
            <p>I come from BioScience, and I’m learning to build software. The eagle keeps a little of that first world in this new one. For me, it’s a reminder to stay curious, step back when I get stuck, and keep going while I’m still figuring things out.</p>
            <p className="eagle-personal-line">Observe carefully. Build patiently. Keep exploring.</p>
          </section>

          <section className="eagle-facts" aria-labelledby="eagle-facts-title">
            <p className="eagle-eyebrow">Field notes</p>
            <h2 id="eagle-facts-title">A few things worth knowing.</h2>
            <div className="eagle-fact-grid">
              {facts.map((fact, index) => <article key={fact.title}>
                <span className="eagle-fact-number">0{index + 1}</span>
                <h3>{fact.title}</h3>
                <p>{fact.text}</p>
                <a href={fact.source} target="_blank" rel="noopener noreferrer">{fact.label}</a>
              </article>)}
            </div>
          </section>

          <footer className="eagle-footer">
            <p>A little nature in a corner of the internet.</p>
            <PortfolioPageLink to="/about">Back to the things I’m building</PortfolioPageLink>
          </footer>
        </div>
      </main>
    </div>
  )
}
